import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input, Label, Textarea } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { createAgent, listMyDomains, updateAgent } from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";
import type { AgentView } from "@/types/api";

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

/** Create (when `agent` is undefined) or edit an agent. Controlled dialog. */
export function AgentDialog({
  open,
  onOpenChange,
  agent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent?: AgentView;
}) {
  const qc = useQueryClient();
  const editing = !!agent;
  const domains = useQuery({ queryKey: qk.myDomains, queryFn: listMyDomains, enabled: open });

  const [name, setName] = React.useState("");
  const [domainSlug, setDomainSlug] = React.useState("");
  const [description, setDescription] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setName(agent?.name ?? "");
      setDomainSlug(agent?.domain_slug ?? "");
      setDescription(agent?.description ?? "");
    }
  }, [open, agent]);

  // Default the domain select to the first domain when creating.
  React.useEffect(() => {
    const first = domains.data?.[0];
    if (open && !editing && !domainSlug && first) {
      setDomainSlug(first.slug);
    }
  }, [open, editing, domainSlug, domains.data]);

  const mutation = useMutation({
    mutationFn: () =>
      editing
        ? updateAgent(agent!.slug, {
            name,
            domain_slug: domainSlug,
            description: description || null,
          })
        : createAgent({ name, domain_slug: domainSlug, description: description || null }),
    onSuccess: async () => {
      toast.success(editing ? "Agent updated." : "Agent created.");
      await qc.invalidateQueries({ queryKey: qk.myAgents });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const noDomains = domains.data?.length === 0;
  const canSubmit = !!name.trim() && !!domainSlug && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit agent" : "New agent"}</DialogTitle>
          <DialogDescription>
            An agent belongs to a domain. Its runtime behaviour lives in versions you add next.
          </DialogDescription>
        </DialogHeader>
        {noDomains ? (
          <p className="text-sm text-muted-foreground">
            Create a domain first — an agent must belong to one.
          </p>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="a-name">Name</Label>
              <Input
                id="a-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Triage Bot"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="a-domain">Domain</Label>
              <select
                id="a-domain"
                className={selectClass}
                value={domainSlug}
                onChange={(e) => setDomainSlug(e.target.value)}
              >
                {(domains.data ?? []).map((d) => (
                  <option key={d.slug} value={d.slug}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="a-desc">Description</Label>
              <Textarea
                id="a-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this agent does (optional)"
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending ? "Saving…" : editing ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
